import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Grid,
  Button,
  Divider,
} from '@mui/material';
import { Visibility as ViewIcon } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store/store';
import { fetchOrders } from '../../store/slices/orderSlice';
import { OrderStatus } from '../../types';
import { format } from 'date-fns';
import { bg } from 'date-fns/locale';
import { useSettings } from '../../contexts/SettingsContext';
import { formatPrice as formatPriceForSettings } from '../../utils/currency';

const OrdersPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orders, loading, error } = useSelector((state: RootState) => state.orders);
  const { user } = useSelector((state: RootState) => state.auth);
  const { currencySettings } = useSettings();

  // Ensure orders is always an array
  const safeOrders = Array.isArray(orders) ? orders : [];

  useEffect(() => {
    if (user) {
      dispatch(fetchOrders() as any);
    }
  }, [dispatch, user]);

  const formatPrice = (price: number) => formatPriceForSettings(price, currencySettings);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'd MMM, yyyy', { locale: bg });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getStatusLabel = (status: OrderStatus) => {
    return t(`user.orders.statuses.${status.toLowerCase()}`);
  };

  const getStatusColor = (status: OrderStatus) => {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'CONFIRMED': return 'info';
      case 'PROCESSING': return 'primary';
      case 'SHIPPED': return 'secondary';
      case 'DELIVERED': return 'success';
      case 'CANCELLED': return 'error';
      case 'RETURNED': return 'default';
      default: return 'default';
    }
  };

  if (!user) {
    return (
      <Box>
        <Typography variant="h5">{t('user.orders.loginToViewOrders')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" fontWeight="bold" mb={3}>
        {t('user.orders.title')}
      </Typography>

      {loading && (
        <Typography>{t('user.orders.loadingOrders')}</Typography>
      )}

      {error && (
        <Typography color="error" mb={3}>
          {error}
        </Typography>
      )}

      {safeOrders.length === 0 && !loading && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" gutterBottom>
              {t('user.orders.noOrdersFound')}
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={3}>
              {t('user.orders.noOrdersMessage')}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/shop')}
            >
              {t('user.orders.startShopping')}
            </Button>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={3}>
        {safeOrders.map((order) => (
          <Grid item xs={12} key={order.id}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {t('user.orders.orderNumber')} #{order.orderNumber}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {t('user.orders.placedOn')} {formatDate(order.createdAt!)}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Chip
                      label={getStatusLabel(order.status)}
                      color={getStatusColor(order.status) as any}
                      size="small"
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ViewIcon />}
                      onClick={() => navigate(`/orders/${order.orderNumber}`)}
                    >
                      {t('user.orders.viewDetails')}
                    </Button>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      {t('user.orders.items')}: {order.items?.length || 0}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {t('user.orders.payment')}: {order.paymentMethod?.replace('_', ' ') || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} sx={{ textAlign: { sm: 'right' } }}>
                    <Typography variant="h6" color="primary">
                      {t('user.orders.total')}: {formatPrice(order.totalAmount || 0)}
                    </Typography>
                  </Grid>
                </Grid>

                {(order.items || []).slice(0, 3).map((item, index) => (
                  <Box
                    key={item.id}
                    display="flex"
                    alignItems="center"
                    gap={2}
                    mt={2}
                    p={1}
                    bgcolor="grey.50"
                    borderRadius={1}
                  >
                    <Box
                      component="img"
                      sx={{
                        height: 40,
                        width: 40,
                        borderRadius: 1,
                        objectFit: 'cover',
                      }}
                      src={item.product.imageUrl || '/placeholder-book.svg'}
                      alt={item.product.title}
                      onError={(e: any) => {
                        e.target.src = '/placeholder-book.svg';
                      }}
                    />
                    <Box flex={1}>
                      <Typography variant="body2" fontWeight="medium">
                        {item.product.title}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t('user.orders.qty')}: {item.quantity} × {formatPrice(item.price)}
                      </Typography>
                    </Box>
                  </Box>
                ))}

                {(order.items?.length || 0) > 3 && (
                  <Typography variant="body2" color="textSecondary" mt={1}>
                    +{t('user.orders.moreItems', { count: (order.items?.length || 0) - 3 })}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default OrdersPage;
